import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Gem, TrendingDown, TrendingUp } from 'lucide-react'
import { ResponsiveLine } from '@nivo/line'

/**
 * Строим кумулятивный баланс из my_bets (приходят от новых к старым)
 */
const buildSeries = (myBets) => {
  const safeBets = Array.isArray(myBets) ? myBets : []

  if (safeBets.length === 0) {
    return {
      series: [{ x: 0, y: 0, profit: 0, isWinner: null }],
      roundsUsed: 0,
      totalNet: 0,
      wins: 0,
      losses: 0
    }
  }

  // my_bets приходит от новых к старым → разворачиваем для графика слева направо
  const bets = [...safeBets].reverse()

  let cumulative = 0
  const series = [{ x: 0, y: 0, profit: 0, isWinner: null }]

  bets.forEach((round, index) => {
    const profit = Number(round?.profit || 0)
    cumulative += profit
    series.push({
      x: index + 1,
      y: cumulative,
      profit,
      isWinner: Boolean(round?.is_winner)
    })
  })

  const totalNet = cumulative
  const wins = safeBets.filter((r) => r?.is_winner).length
  const losses = safeBets.filter((r) => !r?.is_winner).length

  return {
    series,
    roundsUsed: safeBets.length,
    totalNet,
    wins,
    losses
  }
}

const DoublePerformanceChart = ({ myBets = [] }) => {
  const { t } = useTranslation()

  const performance = useMemo(() => {
    return buildSeries(myBets)
  }, [myBets])

  const chartData = useMemo(() => {
    return [
      {
        id: 'balance',
        data: performance.series.map((point) => ({
          x: point.x,
          y: point.y,
          profit: point.profit,
          isWinner: point.isWinner
        }))
      }
    ]
  }, [performance.series])

  const totalNetSign = performance.totalNet > 0 ? '+' : performance.totalNet < 0 ? '-' : ''
  const totalNetAbs = Math.abs(performance.totalNet)
  const lineColor = performance.totalNet >= 0 ? '#34d399' : '#fb7185'
  const lineAreaColor = performance.totalNet >= 0 ? 'rgba(52,211,153,0.18)' : 'rgba(251,113,133,0.18)'

  return (
    <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
      <div>
        <div>
          <h3 className="text-sm font-semibold text-white">
            {t('double.performance.title', 'Баланс моей игры')}
          </h3>
        </div>
      </div>

      {performance.roundsUsed > 0 ? (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
              <p className="text-text-secondary">{t('double.performance.total', 'Итог')}</p>
              <p className={`mt-1 inline-flex items-center gap-1 font-semibold ${
                performance.totalNet > 0
                  ? 'text-emerald-300'
                  : performance.totalNet < 0
                    ? 'text-rose-300'
                    : 'text-white'
              }`}>
                <Gem className="h-3.5 w-3.5" />
                <span>{totalNetSign}{totalNetAbs.toLocaleString()}</span>
              </p>
            </div>

            <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
              <p className="text-text-secondary">{t('double.performance.wins', 'Победы')}</p>
              <p className="mt-1 inline-flex items-center gap-1 font-semibold text-emerald-300">
                <TrendingUp className="h-3.5 w-3.5" />
                <span>{performance.wins}</span>
              </p>
            </div>

            <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
              <p className="text-text-secondary">{t('double.performance.losses', 'Поражения')}</p>
              <p className="mt-1 inline-flex items-center gap-1 font-semibold text-rose-300">
                <TrendingDown className="h-3.5 w-3.5" />
                <span>{performance.losses}</span>
              </p>
            </div>
          </div>

          <div className="mt-3 h-28 w-full rounded-xl border border-white/10 bg-dark-primary/40 p-2">
            <div className="h-full w-full" role="img" aria-label={t('double.performance.aria', 'График баланса игры в Double')}>
              <ResponsiveLine
                data={chartData}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                xScale={{ type: 'point' }}
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: false,
                  reverse: false
                }}
                axisTop={null}
                axisRight={null}
                axisBottom={null}
                axisLeft={null}
                enableGridX={false}
                enableGridY={false}
                curve="monotoneX"
                colors={[lineColor]}
                lineWidth={3}
                enablePoints={false}
                useMesh
                animate={false}
                isInteractive
                enableArea
                areaOpacity={1}
                areaBaselineValue={0}
                defs={[
                  {
                    id: 'doublePerformanceArea',
                    type: 'linearGradient',
                    colors: [
                      { offset: 0, color: lineAreaColor },
                      { offset: 100, color: 'rgba(0,0,0,0)' }
                    ]
                  }
                ]}
                fill={[{ match: '*', id: 'doublePerformanceArea' }]}
                markers={[
                  {
                    axis: 'y',
                    value: 0,
                    lineStyle: {
                      stroke: 'rgba(148,163,184,0.45)',
                      strokeWidth: 1,
                      strokeDasharray: '4 4'
                    }
                  }
                ]}
                tooltip={({ point }) => {
                  const rawY = Number(point?.data?.y ?? 0)
                  const rawProfit = Number(point?.data?.profit ?? 0)
                  const ySign = rawY > 0 ? '+' : rawY < 0 ? '-' : ''
                  const profitSign = rawProfit > 0 ? '+' : rawProfit < 0 ? '-' : ''

                  return (
                    <div className="rounded-lg border border-white/15 bg-dark-primary/95 px-2 py-1.5 text-xs text-white shadow-xl">
                      <div className="text-text-secondary">
                        {t('double.performance.point', 'Игра')} #{point?.data?.x}
                      </div>
                      <div className="mt-0.5 font-semibold">
                        {t('double.performance.total', 'Итог')}: {ySign}{Math.abs(rawY).toLocaleString()} <Gem className="inline h-3 w-3" />
                      </div>
                      <div className="text-text-secondary">
                        {t('double.performance.delta', 'Изменение')}: {profitSign}{Math.abs(rawProfit).toLocaleString()} <Gem className="inline h-3 w-3" />
                      </div>
                    </div>
                  )
                }}
                theme={{
                  tooltip: {
                    container: {
                      background: 'transparent',
                      boxShadow: 'none',
                      padding: 0
                    }
                  },
                  crosshair: {
                    line: {
                      stroke: 'rgba(148,163,184,0.45)',
                      strokeWidth: 1,
                      strokeDasharray: '3 3'
                    }
                  }
                }}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="mt-3 rounded-xl border border-white/10 bg-dark-primary/40 p-3 text-xs text-text-secondary">
          {t('double.performance.empty', 'Сделайте первую ставку, чтобы увидеть график баланса вашей игры')}
        </div>
      )}
    </div>
  )
}

export default DoublePerformanceChart
